import { useEffect, useState } from "react";
import TablaEquiposVista from "../vista/tablaEquiposVista";
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type SortingState } from "@tanstack/react-table";
import { columnasTablaEquipo } from "../vista/constantesTablaEquipos";
import type { IEquipo } from "../../../modelos/Equipo/entidades/IEquipo";
import { estadoInicialEquipo, type IEntidadesEquipo, type IEquiposCVProps } from "../interfaces";
import React from "react";
import { EquipoModelo } from "../../../modelos/Equipo";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { useConfirmacion } from "../../../globales/contextos/confirmacionContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import { EnumTipoEquipo } from "../enums/enumTipoEquipo";
import type { IFiltroEquipo } from "../../../modelos/Equipo/entidades/IFiltroEquipo";

const EquiposCV: React.FunctionComponent<IEquiposCVProps> = ({ idProyecto, pantallaCompleta }) => {
  const PAGE_SIZE = 8;
  const modeloEquipo = new EquipoModelo();
  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const { manejarCargando } = useCargando();

  const [listaEquipos, setListaEquipos] = useState<IEquipo[]>([]);
  const [habilitarFormulario, setHabilitarFormulario] = useState(false);
  const [filtro, setFiltro] = useState<IFiltroEquipo>({ ProyectoId: idProyecto });

  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [equipo, setEquipo] = useState<IEquipo>({ ...estadoInicialEquipo, ProyectoId: idProyecto });
  const [habilitarFiltros, setHabilitarFiltros] = useState(false);
  const [entidadesEquipo, setEntidadesEquipo] = useState<IEntidadesEquipo>({
    EstadosEquipo: [],
    MarcasEquipo: [],
    ModelosEquipoFiltrados: [],
    ModelosEquipo: [],
    TiposEquipo: []
  });
  const [errores, setErrores] = useState<Partial<Record<keyof IEquipo, string | null>>>({});

  useEffect(() => {
    consultarDatosEquipos();
  }, []);

  useEffect(() => {
    if (!habilitarFormulario) {
      setEquipo({ ...estadoInicialEquipo, ProyectoId: idProyecto });
      setErrores({});
      consultarEquipos();
    }
  }, [habilitarFormulario])

  const consultarEquipos = async (filtrosConsulta?: IFiltroEquipo) => {
    try {
      manejarCargando(true);
      const filtrosAUsar = filtrosConsulta || filtro;

      // Asegurarnos de que siempre incluimos el ProyectoId
      const filtrosFinales: IFiltroEquipo = {
        ...filtrosAUsar,
        ProyectoId: idProyecto
      };

      // Limpiar valores undefined o vacíos
      (Object.keys(filtrosFinales) as Array<keyof IFiltroEquipo>).forEach(key => {
        const valor = filtrosFinales[key];
        if (valor === undefined || valor === "") {
          delete filtrosFinales[key];
        }
      });

      const equipos = await modeloEquipo.obtenerListaEquipos(filtrosFinales);

      if (equipos && equipos.Data) {
        setListaEquipos(equipos.Data.filter(e => !e.Eliminado));
      } else {
        setListaEquipos([]);
      }
    }
    catch (error) {
      console.error("Error al consultar equipos:", error);
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error de conexión',
        'No se pudo conectar con el servidor. Por favor intenta más tarde.',
        5000
      );
      setListaEquipos([]);
    } finally {
      manejarCargando(false);
    }
  }

  const consultarDatosEquipos = async () => {
    const [tiposResultado, marcasResultado, estadosResultado, modelosResultado] = await Promise.allSettled([
      modeloEquipo.obtenerListaTiposEquipos(),
      modeloEquipo.obtenerListaMarcasEquipo(),
      modeloEquipo.obtenerListaEstadosEquipo(),
      modeloEquipo.obtenerListaModelosEquipo()
    ]);
    setEntidadesEquipo((prev) => ({
      ...prev,
      TiposEquipo: tiposResultado.status === "fulfilled" ? tiposResultado.value.Data : [],
      MarcasEquipo: marcasResultado.status === "fulfilled" ? marcasResultado.value.Data : [],
      EstadosEquipo: estadosResultado.status === "fulfilled" ? estadosResultado.value.Data : [],
      ModelosEquipo: modelosResultado.status === "fulfilled" ? modelosResultado.value.Data : []
    }));
  };

  const filtrarModelosPorMarca = (idMarca: number) => {
    setEntidadesEquipo((prev) => ({
      ...prev,
      ModelosEquipoFiltrados: prev.ModelosEquipo.filter(m => m.MarcaId === idMarca)
    }));
  }

  const validarCamposEquipo = () => {
    let esValido = true;

    if (!equipo.Nombre.trim()) {
      agregarError("Nombre", "Este campo es requerido");
      esValido = false;
    }

    if (!equipo.Codigo.trim()) {
      agregarError("Codigo", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.TipoEquipoId === 0) {
      agregarError("TipoEquipoId", "Selecciona un tipo de equipo");
      esValido = false;
    }

    if (equipo.ModeloId === 0) {
      agregarError("ModeloId", "Selecciona un modelo");
      esValido = false;
    }

    if (equipo.MarcaId === 0) {
      agregarError("MarcaId", "Selecciona una marca");
      esValido = false;
    }

    if (equipo.Paradas === 0 && equipo.TipoEquipoId === EnumTipoEquipo.Ascensor) {
      agregarError("Paradas", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.Pisos === 0 && equipo.TipoEquipoId === EnumTipoEquipo.Ascensor) {
      agregarError("Pisos", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.Entradas === 0 && equipo.TipoEquipoId === EnumTipoEquipo.Ascensor) {
      agregarError("Entradas", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.Capacidad === 0 && equipo.TipoEquipoId === EnumTipoEquipo.Ascensor) {
      agregarError("Capacidad", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.Angulo === 0 && equipo.TipoEquipoId !== EnumTipoEquipo.Ascensor) {
      agregarError("Angulo", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.Altura === 0 && equipo.TipoEquipoId !== EnumTipoEquipo.Ascensor) {
      agregarError("Altura", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.Ancho === 0 && equipo.TipoEquipoId !== EnumTipoEquipo.Ascensor) {
      agregarError("Ancho", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.Velocidad === 0) {
      agregarError("Velocidad", "Este campo es requerido");
      esValido = false;
    }

    if (equipo.EstadoEquipoId === 0) {
      agregarError("EstadoEquipoId", "Selecciona un estado");
      esValido = false;
    }

    if (!esValido) {
      mostrarNotificacion(
        EnumTipoNotificacion.Warning,
        "Campos requeridos (*)",
        "Por favor completa todos los campos antes de continuar.",
        3000
      );
    }


    return esValido;
  };

  const crearEquipo = async () => {
    if (!validarCamposEquipo()) return
    try {
      const proyectoRespuesta = await modeloEquipo.crearEquipo({ ...equipo, ProyectoId: idProyecto, InstaladoUtc: new Date().toISOString(), CreadoUtc: new Date().toISOString(), ActualizadoUtc: new Date().toISOString() });
      if (proyectoRespuesta.NotificationType === EnumTipoNotificacion.Success && proyectoRespuesta.Data) {
        setEquipo(estadoInicialEquipo)
        consultarEquipos()
      }
      if (proyectoRespuesta.Messages.length > 0) {
        proyectoRespuesta.Messages.forEach(mensaje =>
          mostrarNotificacion(
            proyectoRespuesta.NotificationType,
            '',
            mensaje,
            4000
          ));
      }

    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error de conexión',
        'No se pudo conectar con el servidor. Por favor intenta más tarde.',
        5000
      );
    }
  }

  const editarEquipo = async () => {
    if (!validarCamposEquipo()) return
    try {
      const proyectoRespuesta = await modeloEquipo.editarEquipo({ ...equipo, ProyectoId: idProyecto });
      if (proyectoRespuesta.NotificationType === EnumTipoNotificacion.Success) {
        setEquipo(estadoInicialEquipo)
        consultarEquipos()
      }
      if (proyectoRespuesta.Messages.length > 0) {
        proyectoRespuesta.Messages.forEach(mensaje =>
          mostrarNotificacion(
            proyectoRespuesta.NotificationType,
            '',
            mensaje,
            4000
          ));
      } else if (proyectoRespuesta.NotificationType === EnumTipoNotificacion.Success) {
        mostrarNotificacion(
          EnumTipoNotificacion.Success,
          'Edición exitosa',
          'El equipo se ha editado correctamente.',
        )
      }

    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error de conexión',
        'No se pudo conectar con el servidor. Por favor intenta más tarde.',
        5000
      );
    }
  }

  const eliminarEquipo = async (equipo: IEquipo) => {
    const confirmado = await confirmar({
      titulo: `Eliminar equipo`,
      mensaje: `¿Está seguro de que desea eliminar el equipo ${equipo.Codigo}? Esta acción no se puede deshacer.`,
      textoAceptar: "Sí, eliminar",
      textoCancelar: "Cancelar",
      opcionCancelar: true
    });
    if (confirmado) {
      try {
        const proyectoRespuesta = await modeloEquipo.eliminarEquipo(equipo);
        if (proyectoRespuesta.NotificationType === EnumTipoNotificacion.Success) {
          consultarEquipos()
        }
        if (proyectoRespuesta.Messages.length > 0) {
          proyectoRespuesta.Messages.forEach(mensaje =>
            mostrarNotificacion(
              proyectoRespuesta.NotificationType,
              '',
              mensaje,
              4000
            ));
        }

      }
      catch {
        mostrarNotificacion(
          EnumTipoNotificacion.Error,
          'Error de conexión',
          'No se pudo conectar con el servidor. Por favor intenta más tarde.',
          5000
        );
      }
    }
  }


  const crearEditarEquipo = () => {
    if (equipo.Id === 0) {
      crearEquipo();
    }
    else {
      editarEquipo();
    }
  }

  const agregarError = async (campo: keyof IEquipo, error: string) => {
    setErrores((prevErrores) => ({
      ...prevErrores,
      [campo]: error
    }));

  }

  const tabla = useReactTable({
    data: listaEquipos,
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex, pageSize: PAGE_SIZE }, sorting },
    onPaginationChange: updater => {
      if (typeof updater === 'function') {
        const newState = updater({ pageIndex, pageSize: PAGE_SIZE });
        setPageIndex(newState.pageIndex);
      } else {
        setPageIndex(updater.pageIndex ?? 0);
      }
    },
    pageCount: Math.ceil(listaEquipos.length / PAGE_SIZE),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
  });

  const columns = columnasTablaEquipo({
    manejarEdicionEquipo: (equipo) => {
      setEquipo(equipo);
      if (equipo.MarcaId) {
        filtrarModelosPorMarca(equipo.MarcaId);
      }
      setHabilitarFormulario(true);
    },
    manejarEliminacionEquipo: (equipo) => eliminarEquipo(equipo)
  });

  tabla.setOptions((prev) => ({ ...prev, columns }));

  const limpiarErrores = (campos: (keyof IEquipo)[]) => {
    campos.forEach(campo => agregarError(campo, ''));
  };


  const manejarCambioValorEquipo = async (
    campo: keyof IEquipo,
    valor: IEquipo[keyof IEquipo]
  ) => {
    setEquipo((prevEquipo) => {
      let nuevoEquipo = { ...prevEquipo, [campo]: valor };

      // --- CAMBIO DE MARCA ---
      if (campo === "MarcaId") {
        const marcaId = Number(valor);

        if (!marcaId) {
          return { ...nuevoEquipo, ModeloId: 0 };
        }
        filtrarModelosPorMarca(marcaId);
        return { ...nuevoEquipo, ModeloId: 0 };
      }

      if (campo === "TipoEquipoId") {
        const tipoId = Number(valor);

        if (!tipoId) {
          return {
            ...nuevoEquipo,
            TipoEquipoId: 0,
            Pisos: 0,
            Paradas: 0,
            Entradas: 0,
            Capacidad: 0,
            Angulo: 0,
            Altura: 0,
            Ancho: 0,
          };
        }

        const esAscensor = tipoId === EnumTipoEquipo.Ascensor;

        if (esAscensor) {
          limpiarErrores(['Angulo', 'Altura', 'Ancho']);
          nuevoEquipo = {
            ...nuevoEquipo,
            Pisos: 0,
            Paradas: 0,
            Entradas: 0,
            Capacidad: 0,
          };
        } else {
          limpiarErrores(['Pisos', 'Paradas', 'Entradas', 'Capacidad']);
          nuevoEquipo = {
            ...nuevoEquipo,
            Angulo: 0,
            Altura: 0,
            Ancho: 0,
          };
        }
      }

      return nuevoEquipo;
    });
  };


  const manejarHabilitarFiltros = (habilitar: boolean) => {
    setHabilitarFiltros(habilitar)
  }

  const manejarCambioFiltroEquipo = (campo: keyof IEquipo, valor: IEquipo[keyof IEquipo]) => {
    setFiltro((prevFiltro) => ({
      ...prevFiltro,
      [campo]: valor
    }));
  }

  const limpiarFiltros = () => {
    const filtrosLimpios = { ProyectoId: idProyecto };
    setFiltro(filtrosLimpios);
    consultarEquipos(filtrosLimpios);
  };

  const descargarPlantilla = async () => {
    try {
      manejarCargando(true, undefined, true);

      await modeloEquipo.descargarPlantilla();

      mostrarNotificacion(
        EnumTipoNotificacion.Success,
        "Descarga completada",
        "La plantilla de equipos se descargó correctamente.",
        4000
      );
    } catch (error) {
      console.error("Error al descargar la plantilla de equipos:", error);
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        "Error al descargar",
        "No fue posible descargar la plantilla. Verifica tu conexión e intenta nuevamente.",
        5000
      );
    } finally {
      manejarCargando(false);
    }
  };


  const cargaMasivaEquipos = async () => {
    try {
      // Crear input de archivo (oculto)
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx";

      input.onchange = async (e: any) => {
        const archivo = e.target.files[0];
        if (!archivo) return;

        try {
          manejarCargando(true, "Cargando archivo de equipos...", true);

          const respuesta = await modeloEquipo.cargarMasiva(archivo);

          // Mostrar mensajes del backend (si existen)
          if (respuesta?.Messages?.length > 0) {
            respuesta.Messages.forEach((mensaje) =>
              mostrarNotificacion(
                respuesta.NotificationType ?? EnumTipoNotificacion.Information,
                "",
                mensaje,
                4000
              )
            );
          } else {
            mostrarNotificacion(
              EnumTipoNotificacion.Success,
              "Carga masiva completada",
              "El archivo de equipos se cargó correctamente.",
              4000
            );
          }
        } catch (error) {
          console.error("Error durante la carga masiva de equipos:", error);
          mostrarNotificacion(
            EnumTipoNotificacion.Error,
            "Error al cargar",
            "No fue posible procesar el archivo. Verifica el formato o la conexión.",
            5000
          );
        } finally {
          manejarCargando(false);
        }
      };

      // Disparar selector
      input.click();
    } catch (error) {
      console.error("Error inicial en carga masiva de equipos:", error);
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        "Error inesperado",
        "Ocurrió un error al intentar seleccionar el archivo.",
        5000
      );
    }
  };

// equiposCV.tsx

return (
 <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
    <TablaEquiposVista
      tabla={tabla}
      manejarHabilitarFiltros={manejarHabilitarFiltros}
      habilitarFormulario={habilitarFormulario}
      manejarHabilitarFormulario={setHabilitarFormulario}
      descargarPlantilla={descargarPlantilla}
      cargaMasivaEquipos={cargaMasivaEquipos}
      agregarEditarEquipo={crearEditarEquipo}
      entidadesEquipo={entidadesEquipo}
      equipoSeleccionado={equipo}
      manejarCambioValorEquipo={manejarCambioValorEquipo}
      agregarError={agregarError}
      errores={errores}
      consultarEquipos={consultarEquipos}
      filtro={filtro}
      manejarCambioFiltroEquipo={manejarCambioFiltroEquipo}
      limpiarFiltros={limpiarFiltros}
      habilitarFiltros={habilitarFiltros}
      pantallaCompleta={pantallaCompleta}
    />
  </div>
);
}

export default React.memo(EquiposCV);